// ./src/hooks/usePartnerApplications.ts
//
// The inbound sponsorship applications on one event (#A4.5).
//
// ⚠ READ + DECIDE ONLY. An application arrives from the PUBLIC event website; a host never creates,
// edits or deletes one. So this hook deliberately exposes no create/update/remove — the shape it was
// cloned from had all three, and carrying them here would offer a host operations the server has no
// intention of honouring.
import { useState, useCallback, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  PartnerApplication,
  PartnerApplicationService,
  PartnerApplicationFilter,
  Pagination,
  SortItem,
} from "@/utils/api";
import { upsertBy, upsertManyBy } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

/**
 * The action keys for the two review decisions.
 *
 * ⚠ Exported so the row reads back exactly what the hook stored. Keyed per ACTION and not per row
 * because a row carries BOTH buttons — keyed by id alone, approving would spin the Decline button
 * too, and on a screen where one of them moves money that is worse than a missing spinner.
 */
export const APPLICATION_APPROVE_ACTION = "approve";
export const APPLICATION_REJECT_ACTION = "reject";
export const APPLICATION_WITHDRAW_ACTION = "withdraw";

export interface UsePartnerApplicationsOptions {
  pageSize?: number;
  initialFilters?: Partial<PartnerApplicationFilter>;
  autoLoad?: boolean;
}

export interface UsePartnerApplicationsReturn {
  partnerApplications: PartnerApplication[];
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;

  /** Both args → that one action; id only → any decision on that row; neither → anything in flight. */
  isDeciding: (id?: string, actionKey?: string) => boolean;

  list: (
    filter?: Partial<PartnerApplicationFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  get: (id: string) => Promise<OpResult>;
  /** Takes a place on the offering and creates the confirmed partner. */
  approve: (id: string, reviewNotes?: string | null) => Promise<OpResult>;
  /** ⚠ REFUNDS a sponsor who already paid. A failed refund fails the whole rejection. */
  reject: (id: string, reviewNotes?: string | null) => Promise<OpResult>;
  /** ⚠ `refundApproved` applies only to an APPROVED application; a pending one always refunds. */
  withdraw: (id: string, reviewNotes?: string | null, refundApproved?: boolean) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  getById: (id: string | undefined) => PartnerApplication | undefined;

  lastError: OpResult | null;
  clearError: () => void;
}

export const usePartnerApplications = (options: UsePartnerApplicationsOptions = {}): UsePartnerApplicationsReturn => {
  const { pageSize = DEFAULT_PAGE_SIZE, initialFilters, autoLoad = false } = options;

  const [partnerApplications, setPartnerApplications] = useState<PartnerApplication[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state before the
   * skeleton and then the data. Reported across the whole event space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [decidingKeys, setDecidingKeys] = useState<Set<string>>(new Set());
  const [lastError, setLastError] = useState<OpResult | null>(null);

  const filtersRef = useRef<Partial<PartnerApplicationFilter>>(initialFilters || {});
  const sortRef = useRef<SortItem[] | undefined>(undefined);
  // ⚠ Guards a slow response from overwriting a newer one — a host flipping filters quickly would
  // otherwise see the previous filter's rows land on top of the current ones.
  const requestRef = useRef(0);

  const isDeciding = useCallback(
    (id?: string, actionKey?: string): boolean => {
      if (!id) {
        return decidingKeys.size > 0;
      }

      if (actionKey) {
        return decidingKeys.has(`${id}:${actionKey}`);
      }

      return [...decidingKeys].some((k) => k.startsWith(`${id}:`));
    },
    [decidingKeys]
  );

  const list = useCallback(
    async (
      filter?: Partial<PartnerApplicationFilter>,
      pagination?: Pagination,
      sortBy?: SortItem[],
      shouldReset = true
    ): Promise<OpResult> => {
      const page = pagination?.page ?? 1;
      const ticket = ++requestRef.current;

      if (shouldReset) {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#119).
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      filtersRef.current = filter ?? filtersRef.current;
      sortRef.current = sortBy ?? sortRef.current;

      try {
        const result = await PartnerApplicationService.list(
          filtersRef.current as PartnerApplicationFilter,
          { page, pageSize },
          sortRef.current
        );

        if (ticket !== requestRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
          const rows = result.data as PartnerApplication[];
          setPartnerApplications((prev) => (shouldReset ? rows : upsertManyBy(prev, rows, "id", "end")));
          setCurrentPage(page);
          // `total` is the whole filtered set, so "is there another page" is a comparison against
          // everything loaded so far — not against this page's length.
          const loaded = shouldReset ? rows.length : partnerApplications.length + rows.length;
          setHasMore(typeof result.total === "number" ? loaded < result.total : rows.length === pageSize);
          setLastError(null);
        } else {
          setLastError(result);
        }

        setIsLoaded(true);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);

        // A failure of a superseded read is not this list's failure either (#119).
        if (ticket !== requestRef.current) {
          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        return result;
      } finally {
        if (ticket === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [pageSize, partnerApplications.length]
  );

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(() => list(filtersRef.current, { page: 1, pageSize }, sortRef.current, true), isLoaded);

  const get = useCallback(async (id: string): Promise<OpResult> => {
    const result = await PartnerApplicationService.get(id);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data) && result.data[0]) {
      setPartnerApplications((prev) => upsertBy(prev, result.data[0] as PartnerApplication));
    }

    return result;
  }, []);

  /**
   * Run one review decision, sharing the in-flight machinery so each button spins alone.
   *
   * ⚠ These are NOT status updates. Approving takes a place on the offering under a row lock and
   * creates the confirmed partner; rejecting REFUNDS a sponsor who already paid. The server owns
   * both — this carries only the id and an optional note.
   */
  const runReview = useCallback(
    async (
      id: string,
      actionKey: string,
      fn: (id: string, reviewNotes?: string | null) => Promise<OpResult>,
      reviewNotes?: string | null
    ): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "An application is required");
        setLastError(result);

        return result;
      }

      const key = `${id}:${actionKey}`;
      setDecidingKeys((prev) => new Set(prev).add(key));
      setLastError(null);

      try {
        const result = await fn(id, reviewNotes ?? null);

        if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data) && result.data[0]) {
          // Patch the row from what the server returned rather than reloading — the decision, the
          // reviewer and the timestamp all come back on it.
          setPartnerApplications((prev) => upsertBy(prev, result.data[0] as PartnerApplication));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setDecidingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);

          return next;
        });
      }
    },
    []
  );

  const approve = useCallback(
    (id: string, reviewNotes?: string | null) =>
      runReview(id, APPLICATION_APPROVE_ACTION, PartnerApplicationService.approve, reviewNotes),
    [runReview]
  );

  const reject = useCallback(
    (id: string, reviewNotes?: string | null) =>
      runReview(id, APPLICATION_REJECT_ACTION, PartnerApplicationService.reject, reviewNotes),
    [runReview]
  );

  /**
   * ⚠ `refundApproved` rides through `runReview` as a bound argument rather than a fourth parameter
   * on the shared runner — every other decision takes (id, notes), and widening the runner for one
   * caller is how the in-flight keying and local patching start diverging per action.
   */
  const withdraw = useCallback(
    (id: string, reviewNotes?: string | null, refundApproved?: boolean) =>
      runReview(
        id,
        APPLICATION_WITHDRAW_ACTION,
        (applicationId: string, notes?: string | null) =>
          PartnerApplicationService.withdraw(applicationId, notes, refundApproved),
        reviewNotes
      ),
    [runReview]
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) {
      return;
    }

    await list(filtersRef.current, { page: currentPage + 1, pageSize }, sortRef.current, false);
  }, [hasMore, loadingMore, loading, list, currentPage, pageSize]);

  const getById = useCallback(
    (id: string | undefined) => (id ? partnerApplications.find((a) => a.id === id) : undefined),
    [partnerApplications]
  );

  const clearError = useCallback(() => setLastError(null), []);

  useEffect(() => {
    if (autoLoad) {
      list(initialFilters, { page: 1, pageSize });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  return {
    partnerApplications,
    loading,
    isLoaded,
    loadingMore,
    hasMore,
    isDeciding,
    list,
    get,
    approve,
    reject,
    withdraw,
    loadMore,
    getById,
    lastError,
    clearError,
  };
};
